import {
  addDoc,
  collection,
  doc,
  getDoc,
  increment,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db, getUserDisplayName } from '../firebase';

/**
 * Default profile stored at users/{uid}.
 * Created once; later logins only update lastLoginAt / loginCount.
 */
export function createDefaultUserProfile(user, extras = {}) {
  const displayName = extras.displayName || extras.username || getUserDisplayName(user);

  return {
    uid: user.uid,
    email: user.email || '',
    displayName,
    username: extras.username || '',
    photoUrl: user.photoURL || '',
    providerId: user.providerId || 'password',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastLoginAt: null,
    loginCount: 0,
    preferences: {
      defaultTicker: 'AAPL',
      defaultCrypto: 'X:BTCUSD',
    },
  };
}

export async function ensureUserProfile(user, extras = {}) {
  if (!user?.uid) return null;

  const userRef = doc(db, 'users', user.uid);

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userRef);
    if (!snapshot.exists()) {
      transaction.set(userRef, createDefaultUserProfile(user, extras));
    }
  });

  return userRef;
}

export async function getUserProfile(uid) {
  if (!uid) return null;

  const snapshot = await getDoc(doc(db, 'users', uid));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function recordUserLogin(user, extras = {}) {
  if (!user?.uid) return;

  const userRef = doc(db, 'users', user.uid);
  const displayName = extras.displayName || extras.username || getUserDisplayName(user);

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userRef);

    if (!snapshot.exists()) {
      transaction.set(userRef, {
        ...createDefaultUserProfile(user, extras),
        lastLoginAt: serverTimestamp(),
        loginCount: 1,
      });
      return;
    }

    const updates = {
      lastLoginAt: serverTimestamp(),
      loginCount: increment(1),
      updatedAt: serverTimestamp(),
      email: user.email || '',
      displayName,
      photoUrl: user.photoURL || '',
      providerId: user.providerId || snapshot.data()?.providerId || 'password',
    };

    if (extras.username) {
      updates.username = extras.username;
    }

    transaction.update(userRef, updates);
  });

  await addDoc(collection(db, 'users', user.uid, 'logins'), {
    uid: user.uid,
    email: user.email || '',
    loggedInAt: serverTimestamp(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  });
}
